import app from 'flarum/forum/app';

/**
 * Triggered when the push token is generated by the device.
 */
export const PushToken = 'push-token';

/**
 * Triggered when the user requests permission on the push event.
 */
export const PushPermissionRequest = 'push-permission-request';

/**
 * Returns the state of the push request of the device.
 */
export const PushPermissionState = 'push-permission-state';

type PushPermissionStateType = 'notDetermined' | 'denied' | 'authorized' | 'ephemeral' | 'provisional' | 'unknown' | 'granted';

interface WebKitMessageHandlers {
  [PushToken]: { postMessage: (message: string) => void };
  [PushPermissionRequest]: { postMessage: (message: string) => void };
  [PushPermissionState]: { postMessage: (message: string) => void };
}

interface WebKit {
  messageHandlers: WebKitMessageHandlers;
}

declare global {
  interface Window {
    webkit?: WebKit;
  }
}

interface PushPermissionEvent extends CustomEvent {
  detail: PushPermissionStateType;
}

interface PushTokenEvent extends CustomEvent {
  detail: string;
}

/**
 * Check if the client is a webview in an iOS or iPadOS device.
 */
export const usingAppleWebview = (): boolean => !!(window.webkit && window.webkit.messageHandlers);

export const requestPushPermissionState = (): void => {
  if (usingAppleWebview()) {
    window.webkit!.messageHandlers[PushPermissionState].postMessage(PushPermissionState);
  }
};

export const requestPushPermissions = (): void => {
  if (usingAppleWebview()) {
    window.webkit!.messageHandlers[PushPermissionRequest].postMessage(PushPermissionRequest);
  }
};

export const requestPushToken = (): void => {
  if (usingAppleWebview()) {
    window.webkit!.messageHandlers[PushToken].postMessage(PushToken);
  }
};

export interface PWABuilderHooks {
  hasFirebasePushState: (state: PushPermissionStateType) => boolean;
  registerFirebasePushNotificationListeners: () => void;
  removeFirebasePushNotificationListeners: () => void;
}

export const usePWABuilder = (): PWABuilderHooks => {
  let permissionState: PushPermissionStateType = 'granted';

  const handlePushPermissionRequest = (event: Event): void => {
    const customEvent = event as PushPermissionEvent;
    if (customEvent.detail !== 'granted') {
      return;
    }

    permissionState = 'granted';
    requestPushToken();
  };

  const handlePushToken = (event: Event): void => {
    const customEvent = event as PushTokenEvent;
    app.request({
      method: 'POST',
      url: app.forum.attribute<string>('apiUrl') + '/pwa/firebase-push-subscriptions',
      body: {
        token: customEvent.detail,
      },
    });
  };

  const handlePushPermissionState = (event: Event): void => {
    const customEvent = event as PushPermissionEvent;
    permissionState = customEvent.detail;
    m.redraw();
  };

  const hasFirebasePushState = (state: PushPermissionStateType): boolean => state === permissionState;

  function registerFirebasePushNotificationListeners(): void {
    if (!usingAppleWebview()) {
      return;
    }

    requestPushPermissionState();

    window.addEventListener(PushPermissionRequest, handlePushPermissionRequest);
    window.addEventListener(PushPermissionState, handlePushPermissionState);
    window.addEventListener(PushToken, handlePushToken);
  }

  function removeFirebasePushNotificationListeners(): void {
    if (!usingAppleWebview()) {
      return;
    }

    window.removeEventListener(PushPermissionRequest, handlePushPermissionRequest);
    window.removeEventListener(PushPermissionState, handlePushPermissionState);
    window.removeEventListener(PushToken, handlePushToken);
  }

  return { hasFirebasePushState, registerFirebasePushNotificationListeners, removeFirebasePushNotificationListeners };
};
